/**
 * Product PDP links: #PdpNavLoader snaps to full screen (curtain color from data-pdp-curtain), then PDP ~0.01s later.
 * sessionStorage `blend` + `tathaPdpCurtainHex` for bridge + PDP handoff (main-product).
 */
(() => {
  if (typeof window === 'undefined' || window.__tathaPdpCurtainNav) return;
  window.__tathaPdpCurtainNav = true;

  const DEFAULT_CURTAIN_HEX = '#ffffff';
  const HEX_RE = /^#[0-9a-fA-F]{6}$/;

  /** Fullscreen curtain zoom — long enough to read as intentional motion (not a loading flash). */
  const ZOOM_MS = 520;
  const ZOOM_EASE = 'cubic-bezier(0.22, 1, 0.32, 1)';
  /** Brief hold at scale(1) so the curtain “lands” before the document swap. */
  const POST_FULLSCREEN_MS = 72;

  const normalizeCurtainHex = (raw) => {
    if (!raw || typeof raw !== 'string') return DEFAULT_CURTAIN_HEX;
    const t = raw.trim();
    if (!HEX_RE.test(t)) return DEFAULT_CURTAIN_HEX;
    return t;
  };

  const curtainHexFromAnchor = (anchor) => {
    if (!anchor || !anchor.getAttribute) return DEFAULT_CURTAIN_HEX;
    let h = anchor.getAttribute('data-pdp-curtain');
    if (!h && anchor.closest) {
      const root = anchor.closest(
        '.explore-section .product-card, .collection-overlay-card, .card-wrapper.product-card-wrapper'
      );
      if (root) {
        const withData = root.querySelector('a[href][data-pdp-curtain]');
        if (withData) h = withData.getAttribute('data-pdp-curtain');
      }
    }
    return normalizeCurtainHex(h);
  };

  const isLightCurtainHex = (hex) => {
    const lc = (hex || '').toLowerCase();
    return lc === '#ffffff' || lc === '#fff';
  };

  const isProductPageUrl = (href) => {
    if (!href || href.startsWith('#') || href.startsWith('javascript:')) return false;
    try {
      const u = new URL(href, window.location.href);
      if (u.origin !== window.location.origin) return false;
      if (u.pathname.includes('/cart')) return false;
      return /\/products\/[^/]+/.test(u.pathname);
    } catch {
      return false;
    }
  };

  const firstProductAnchorIn = (root) => {
    if (!root || !root.querySelector) return null;
    const links = root.querySelectorAll('a[href]');
    for (let i = 0; i < links.length; i++) {
      const el = links[i];
      if (el.closest('modal-dialog')) continue;
      const h = el.getAttribute('href');
      if (h && isProductPageUrl(h)) return el;
    }
    return null;
  };

  const resolveProductNavAnchor = (target) => {
    if (!target || !target.closest) return null;
    if (target.closest('cart-drawer') || target.closest('modal-dialog')) return null;

    const direct = target.closest('a[href]');
    if (direct) {
      if (direct.target === '_blank' || direct.hasAttribute('download')) return null;
      if (direct.getAttribute('data-skip-pdp-curtain') === 'true') return null;
      const h = direct.getAttribute('href');
      if (h && isProductPageUrl(h) && !direct.closest('modal-dialog')) return direct;
    }

    if (target.closest('button, [type="submit"], .product-card__form, label[for], input, textarea, select')) {
      return null;
    }

    const exploreCard = target.closest('.explore-section .product-card');
    if (exploreCard) {
      const full = exploreCard.querySelector('a.product-card__full-link[href]');
      if (full && isProductPageUrl(full.getAttribute('href'))) return full;
    }

    const collectionCard = target.closest('.collection-overlay-card');
    if (collectionCard) {
      const a = firstProductAnchorIn(collectionCard);
      if (a) return a;
    }

    const cardWrapper = target.closest('.card-wrapper.product-card-wrapper');
    if (cardWrapper) {
      const a = firstProductAnchorIn(cardWrapper);
      if (a) return a;
    }

    return null;
  };

  const findCardRoot = (clickTarget, anchor) => {
    const pick = (el) =>
      el &&
      el.closest &&
      (el.closest('.explore-section .product-card') ||
        el.closest('.collection-overlay-card') ||
        el.closest('.card-wrapper.product-card-wrapper'));

    return pick(clickTarget) || pick(anchor);
  };

  let busy = false;
  const prefetchedHrefs = new Set();

  document.addEventListener(
    'pointerenter',
    (e) => {
      if (window.Shopify && Shopify.designMode) return;
      const a = e.target && e.target.closest && e.target.closest('a[href]');
      if (!a || a.target === '_blank' || a.hasAttribute('download')) return;
      if (a.closest('modal-dialog') || a.closest('cart-drawer')) return;
      const h = a.getAttribute('href');
      if (!h || !isProductPageUrl(h)) return;
      let abs;
      try {
        abs = new URL(h, window.location.href).href;
      } catch {
        return;
      }
      if (prefetchedHrefs.has(abs)) return;
      prefetchedHrefs.add(abs);
      const link = document.createElement('link');
      link.rel = 'prefetch';
      link.href = abs;
      document.head.appendChild(link);
    },
    true
  );

  const showLoaderThenGo = (href, clickTarget, anchor) => {
    const curtainHex = curtainHexFromAnchor(anchor);
    const loader = document.getElementById('PdpNavLoader');
    try {
      document.documentElement.style.setProperty('--pdp-curtain-surface', curtainHex);
    } catch {
      /* ignore */
    }

    const card = findCardRoot(clickTarget, anchor);
    let ox = window.innerWidth * 0.5;
    let oy = window.innerHeight * 0.5;
    if (card) {
      const r = card.getBoundingClientRect();
      ox = r.left + r.width * 0.5;
      oy = r.top + r.height * 0.5;
    }
    try {
      const vw = window.innerWidth || 1;
      const vh = window.innerHeight || 1;
      sessionStorage.setItem('tathaPdpCurtainOrigin', (ox / vw) * 100 + ',' + (oy / vh) * 100);
    } catch {
      /* ignore */
    }

    if (!loader) {
      try {
        sessionStorage.setItem('tathaPdpCurtainHex', curtainHex);
        sessionStorage.setItem('tathaPdpCurtainNav', 'blend');
      } catch {
        /* ignore */
      }
      window.location.assign(href);
      return;
    }

    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    loader.removeAttribute('hidden');
    loader.setAttribute('aria-hidden', 'false');
    loader.classList.toggle('pdp-nav-loader--dark', !isLightCurtainHex(curtainHex));
    document.documentElement.classList.add('pdp-nav-loader-active');

    const done = () => {
      busy = false;
      /* Do not reset loader colors/transform before assign — that flashes default white behind navy. */
      try {
        sessionStorage.setItem('tathaPdpCurtainHex', curtainHex);
        sessionStorage.setItem('tathaPdpCurtainNav', 'blend');
      } catch {
        /* ignore */
      }
      window.location.assign(href);
    };

    if (reduceMotion) {
      loader.style.transform = 'scale(1)';
      loader.style.transformOrigin = ox + 'px ' + oy + 'px';
      window.setTimeout(done, POST_FULLSCREEN_MS);
      return;
    }

    var zoomFinished = false;
    var postFullTimer = 0;
    const finishZoom = () => {
      if (zoomFinished) return;
      zoomFinished = true;
      loader.removeEventListener('transitionend', onTransformEnd);
      window.clearTimeout(fallbackTimer);
      var runDone = function () {
        postFullTimer = 0;
        done();
      };
      if (POST_FULLSCREEN_MS <= 0) {
        runDone();
      } else {
        postFullTimer = window.setTimeout(runDone, POST_FULLSCREEN_MS);
      }
    };

    const onTransformEnd = (ev) => {
      if (ev.propertyName !== 'transform') return;
      finishZoom();
    };

    loader.addEventListener('transitionend', onTransformEnd);
    var fallbackTimer = window.setTimeout(finishZoom, ZOOM_MS + POST_FULLSCREEN_MS + 160);

    loader.style.transformOrigin = ox + 'px ' + oy + 'px';
    loader.style.transform = 'scale(0.14)';
    loader.style.transition = 'none';
    void loader.offsetHeight;
    loader.style.transition = 'transform ' + ZOOM_MS + 'ms ' + ZOOM_EASE;
    window.requestAnimationFrame(() => {
      window.requestAnimationFrame(() => {
        loader.style.transform = 'scale(1)';
      });
    });
  };

  document.addEventListener(
    'click',
    (e) => {
      if (window.Shopify && Shopify.designMode) return;
      if (e.defaultPrevented) return;
      if (e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || e.button !== 0) return;
      if (busy) return;

      const a = resolveProductNavAnchor(e.target);
      if (!a) return;

      const href = a.getAttribute('href');
      if (!href) return;

      e.preventDefault();
      e.stopPropagation();
      if (typeof e.stopImmediatePropagation === 'function') e.stopImmediatePropagation();

      busy = true;
      showLoaderThenGo(href, e.target, a);
    },
    true
  );

  /**
   * Back from a PDP: the browser resurrects this page from bfcache exactly as we
   * left it — curtain painted over everything, `busy` latched, scroll locked —
   * and no inline script re-runs to take it down. That reads as a blank page, so
   * unwind the nav state here.
   */
  const resetCurtainAfterRestore = () => {
    busy = false;

    const loader = document.getElementById('PdpNavLoader');
    if (loader) {
      loader.style.transition = 'none';
      loader.style.transform = '';
      loader.style.transformOrigin = '';
      loader.classList.remove('pdp-nav-loader--dark');
      loader.setAttribute('hidden', '');
      loader.setAttribute('aria-hidden', 'true');
      void loader.offsetHeight;
      loader.style.transition = '';
    }

    const root = document.documentElement;
    root.classList.remove('pdp-nav-loader-active', 'pdp-instant-bridge', 'pdp-open-curtain-active');
    root.style.removeProperty('--pdp-curtain-surface');
    root.style.removeProperty('--pdp-bridge-bg');

    try {
      document.querySelectorAll('.pdp-open-curtain').forEach((el) => el.remove());
    } catch {
      /* ignore */
    }

    if (document.body) {
      document.body.classList.remove('pdp-handoff-reveal');
      document.body.style.removeProperty('background-color');
      document.body.style.removeProperty('background-image');
    }

    /* A restored page never reaches the PDP handoff, so these would go stale. */
    try {
      sessionStorage.removeItem('tathaPdpCurtainNav');
      sessionStorage.removeItem('tathaPdpCurtainHex');
      sessionStorage.removeItem('tathaPdpCurtainOrigin');
    } catch {
      /* ignore */
    }

    if (typeof window.tathaUnlockDocumentScroll === 'function') {
      window.tathaUnlockDocumentScroll(true);
    }
  };

  window.addEventListener('pageshow', (e) => {
    if (e.persisted) resetCurtainAfterRestore();
  });

  /* Non-bfcache back/forward (Shopify sends no-store on some routes). */
  window.addEventListener('popstate', resetCurtainAfterRestore);
})();
